import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import * as Linking from "expo-linking";
import { useEffect, useState } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useFlags } from "@/utils/launchDarkly";
import { LoadingSpinner } from "@whoosh/design-system/src/components/display/Loader/LoadingSpinner";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { AvailableTeeTimeRow } from "./AvailableTeeTimeRow";
import { AvailableTeeTimesChangedSubscription } from "./AvailableTeeTimesChangedSubscription";
import { NoAvailableTeeTimes } from "./NoAvailableTeeTimes";
import { AvailableTeeTimes_booking$key } from "./__generated__/AvailableTeeTimes_booking.graphql";
import { useGetFullfillableSlots } from "./hooks/useGetFullfillableSlots";
import { useHasUnpublishedRequestRestrictions } from "./hooks/useHasUnpublishedRequestRestrictions";

type Props = {
  bookingId: string;
  bookingFragmentRef: AvailableTeeTimes_booking$key | null;
  bookingType?: string | null;
  hasAvailableMultipleSlotRequestSequence?: boolean;
  requestGroupCount?: number;
};

export const AvailableTeeTimes = ({
  bookingId,
  bookingFragmentRef,
  bookingType,
  hasAvailableMultipleSlotRequestSequence = false,
  requestGroupCount = 1,
}: Props) => {
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();
  const { showToast } = useToast();

  const [launchedFromDeepLink, setLaunchedFromDeepLink] = useState(false);

  const trackEvent = useTrackEvent();

  const shouldShowUnavailableToast =
    route.params?.fromUrl === true ||
    (Platform.OS !== "web" && launchedFromDeepLink);

  const data = useFragment(
    AvailableTeeTimesFragmentString,
    bookingFragmentRef ?? null
  );

  const fullfillableSlots = useGetFullfillableSlots({
    bookingFragmentRef: bookingType === "StandardBooking" ? data : null,
  });
  const { hideUnavailableTimes, requestConfigs } = useFlags();
  const supportsPublic =
    data?.startSlot?.agenda?.facility?.club?.supportsPublic ?? false;
  const clientCanFulfillRequests = supportsPublic
    ? data?.startSlot?.agenda?.facility?.publicBookingPermissionSet
        ?.clientCanFulfillRequests ?? false
    : data?.startSlot?.agenda?.facility?.privateBookingPermissionSet
        ?.clientCanFulfillRequests ?? false;
  const hasBlockingRequestRestrictionStatus =
    !!hideUnavailableTimes &&
    (data?.requestRestrictionStatuses ?? []).some(
      (status) =>
        status?.blockingScope === "FULL" &&
        (status.type === "BLOCK" || status.type === "EVENT")
    );
  const hasUnpublishedRequestRestrictions =
    useHasUnpublishedRequestRestrictions(data?.startSlot ?? null);
  const isPublishedRequestRule =
    !requestConfigs || !hasUnpublishedRequestRestrictions;

  const isDraftBooking = data?.status === "DRAFT";

  useEffect(() => {
    if (
      isPublishedRequestRule &&
      !isDraftBooking &&
      fullfillableSlots?.length &&
      fullfillableSlots?.length > 0
    ) {
      trackEvent("BookingDetail.ArrivedOnBookingSummaryWithFulfillableSlots", {
        requestBookingId: bookingId,
      });
    }
  }, [
    fullfillableSlots,
    trackEvent,
    bookingId,
    isDraftBooking,
    isPublishedRequestRule,
  ]);

  const isRequestBooking = data?.startSlot?.__typename === "QueueSlot";

  useEffect(() => {
    if (
      requestGroupCount > 1 &&
      isRequestBooking &&
      isPublishedRequestRule &&
      !isDraftBooking &&
      !hasBlockingRequestRestrictionStatus &&
      clientCanFulfillRequests &&
      !hasAvailableMultipleSlotRequestSequence
    ) {
      trackEvent("MultipleSlotRequest.RestrictionOrAvailabilityFailed", {
        failureType: "availability",
      });
    }
  }, [
    hasBlockingRequestRestrictionStatus,
    isDraftBooking,
    isPublishedRequestRule,
    isRequestBooking,
    requestGroupCount,
    trackEvent,
    clientCanFulfillRequests,
    hasAvailableMultipleSlotRequestSequence,
  ]);
  const shouldShowCourseName =
    (data?.startSlot?.agenda?.facility?.courses?.count ?? 0) > 1;
  const doWeHaveAvailableTeeTimes =
    isPublishedRequestRule &&
    !!fullfillableSlots?.length &&
    fullfillableSlots.length > 0;

  useEffect(() => {
    let isMounted = true;

    if (Platform.OS === "web") {
      return () => {
        isMounted = false;
      };
    }

    const detectInitialUrl = async () => {
      try {
        const url = await Linking.getInitialURL();
        if (isMounted && url) {
          setLaunchedFromDeepLink(true);
        }
      } catch {
        // Ignore linking errors; fallback to route param check
      }
    };

    detectInitialUrl();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      isPublishedRequestRule &&
      !isDraftBooking &&
      shouldShowUnavailableToast &&
      !doWeHaveAvailableTeeTimes
    ) {
      showToast({
        title: t`Tee Time Unavailable`,
        description: t`We are sorry but the selected Tee Time is not available anymore – it may have already been claimed.`,
        semanticStyle: "error",
      });
    }
  }, [
    shouldShowUnavailableToast,
    showToast,
    doWeHaveAvailableTeeTimes,
    isDraftBooking,
    isPublishedRequestRule,
  ]);

  if (
    requestGroupCount > 1 ||
    !clientCanFulfillRequests ||
    !isRequestBooking ||
    isDraftBooking ||
    !isPublishedRequestRule
  ) {
    return null;
  }

  return (
    <>
      {!doWeHaveAvailableTeeTimes ? (
        <NoAvailableTeeTimes />
      ) : (
        <>
          <Box
            backgroundColor="surface-main-primary-subtle"
            paddingLeft={2}
            paddingRight={2}
            paddingTop={4}
            alignItems="center"
            marginTop={4}
            borderRadius="4"
            paddingBottom={2}
          >
            <LoadingSpinner width={32} height={32} />
            <VStack gap={1} alignItems="center" marginTop={3}>
              <Text
                variant="mainTiny"
                color="text-success"
              >{t`TEE TIME AVAILABLE`}</Text>

              <Text variant="mainBaseSemiBold">{t`Grab an available time before they’re gone!`}</Text>
            </VStack>
            <VStack gap={2} marginTop={4} width="100%">
              {fullfillableSlots?.map((item) => (
                <AvailableTeeTimeRow
                  key={item?.id ?? ""}
                  bookingId={bookingId}
                  time={item?.time ?? null}
                  courseName={item?.course?.name ?? null}
                  shouldShowCourseName={shouldShowCourseName}
                  supportsPublic={supportsPublic}
                  groupSize={data?.rounds?.count ?? 1}
                  slotId={item?.id ?? null}
                  holeCount={
                    data?.rounds?.edges?.[0]?.node?.courseLayout?.holeCount ??
                    "NINE"
                  }
                  slotRates={
                    item?.slotRates?.flatMap((r) =>
                      r
                        ? {
                            nineHolePrice: r?.nineHolePrice ?? 0,
                            eighteenHolePrice: r?.eighteenHolePrice ?? 0,
                            rate: r?.rate ?? { name: "" },
                          }
                        : []
                    ) ?? []
                  }
                />
              ))}
            </VStack>
          </Box>
          <AvailableTeeTimesChangedSubscription bookingId={bookingId} />
        </>
      )}
    </>
  );
};

export const AvailableTeeTimesFragmentString = graphql`
  fragment AvailableTeeTimes_booking on Booking {
    ... on LessonBooking {
      status
    }
    startSlot {
      __typename
      ...useHasUnpublishedRequestRestrictions_slot
      agenda {
        facility {
          club {
            supportsPublic
          }
          courses {
            count
          }
          privateBookingPermissionSet {
            clientCanFulfillRequests
          }
          publicBookingPermissionSet {
            clientCanFulfillRequests
          }
        }
      }
    }
    rounds {
      count
      edges {
        node {
          courseLayout {
            holeCount
          }
        }
      }
    }
    requestRestrictionStatuses {
      type
      blockingScope
    }
    ...useGetFullfillableSlots_booking
    ... on StandardBooking {
      status
    }
  }
`;
